import React, { useEffect, useState } from 'react';
import styles from '../../styles/Product.module.scss';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addItemToCart, toggleForm, toggleFormType } from '../../Store/user/userSlice';
import { addToFavorites, removeFromFavorites, checkFavorite } from '../../Store/favorites/favoritesSlice';
import { assetUrl } from '../../utils/imageUrl';
import { hasDiscount, hasWholesaleTiers, resolveWholesaleTiers } from '../../utils/productPrice';
import {
  getStockLabelText,
  getStockStatusClass,
  isProductPurchasable,
} from '../../utils/productStockStatus';
import ProductReviews from './ProductReviews';

const Product = (item) => {
  const {
    title,
    price,
    oldPrice,
    images,
    description,
    article,
    category,
    inStock = true,
    stockLabel,
    attributes = [],
    id,
    giftProducts = [],
  } = item;

  const wholesaleTiers = resolveWholesaleTiers(item);

  const dispatch = useDispatch();
  const { currentUser } = useSelector(({ user }) => user);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [favoriteNotice, setFavoriteNotice] = useState('');
  const [currentImage, setCurrentImage] = useState();
  const [showWholesale, setShowWholesale] = useState(false);

  useEffect(() => {
    if (!images.length) return;
    setCurrentImage(images[0]);
  }, [images]);

  useEffect(() => {
    if (currentUser && id) {
      dispatch(checkFavorite(id)).then((result) => {
        if (result.payload) {
          setIsFavorite(result.payload.isFavorite);
          setFavoriteId(result.payload.favoriteId);
        }
      });
    }
  }, [currentUser, id, dispatch]);

  const addToCart = () => {
    dispatch(addItemToCart(item));
  };

  const openLogin = () => {
    dispatch(toggleFormType('login'));
    dispatch(toggleForm(true));
  };

  const handleFavoriteToggle = async () => {
    if (!currentUser) {
      openLogin();
      return;
    }

    if (favoriteBusy) return;

    setFavoriteBusy(true);
    setFavoriteNotice('');

    try {
      if (isFavorite) {
        await dispatch(removeFromFavorites({ favoriteId, productId: id })).unwrap();
        setIsFavorite(false);
        setFavoriteId(null);
        setFavoriteNotice('Товар видалено з обраного');
      } else {
        const result = await dispatch(addToFavorites(id)).unwrap();
        setIsFavorite(true);
        if (result.favorite) {
          setFavoriteId(result.favorite.id);
        }
        setFavoriteNotice(result.message || 'Товар додано до обраного');
      }
    } catch (error) {
      const message = typeof error === 'string'
        ? error
        : error?.message || 'Не вдалося оновити обране';
      setFavoriteNotice(message);
      console.error('Помилка при зміні обраного:', error);
    } finally {
      setFavoriteBusy(false);
    }
  };

  const showWholesaleBlock = hasWholesaleTiers(item);
  const canPurchase = isProductPurchasable(stockLabel, inStock);
  const stockStatusText = getStockLabelText(stockLabel, inStock);
  const stockStatusClass = getStockStatusClass(stockLabel, inStock, styles);

  const specs = [
    { label: 'Категорія', value: category?.name },
    { label: 'Код товару', value: article },
    { label: 'Ціна', value: `${price} ₴` },
    hasDiscount(price, oldPrice) && { label: 'Стара ціна', value: `${oldPrice} ₴` },
    ...wholesaleTiers.map((tier) => ({
      label: `Опт від ${tier.minQty} шт.`,
      value: `${tier.price} ₴`,
    })),
    ...attributes.map((row) => ({ label: row.name, value: row.value })),
  ].filter((row) => row && row.value);

  return (
    <section className={styles.product}>
      <h1 className={styles.title}>{title}</h1>

      <div className={styles.top}>
        <div className={styles.gallery}>
          <div className={styles.current}>
            {currentImage && (
              <img src={assetUrl(currentImage)} alt={title} />
            )}
          </div>
          {images.length > 1 && (
            <div className={styles.thumbnails}>
              {images.map((image, i) => (
                <button
                  type="button"
                  key={i}
                  className={`${styles.thumb} ${currentImage === image ? styles.thumbActive : ''}`}
                  onClick={() => setCurrentImage(image)}
                >
                  <img src={assetUrl(image)} alt={`${title} ${i + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.buyBox}>
          <div className={styles.priceBlock}>
            <div className={styles.prices}>
              <div className={styles.price}>{price} ₴</div>
              {hasDiscount(price, oldPrice) && (
                <div className={styles.oldPrice}>{oldPrice} ₴</div>
              )}
            </div>

            {showWholesaleBlock && (
              <div className={styles.wholesaleArea}>
                <button
                  type="button"
                  className={styles.wholesaleToggle}
                  onClick={() => setShowWholesale((v) => !v)}
                >
                  {showWholesale ? 'Сховати оптові ціни' : 'Показати оптові ціни'}
                </button>
                {showWholesale && (
                  <div className={styles.wholesaleTable}>
                    {wholesaleTiers.map((tier) => (
                      <div key={tier.minQty} className={styles.wholesaleRow}>
                        <span>від {tier.minQty} шт.</span>
                        <strong>{tier.price} ₴</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={stockStatusClass}>
            {stockStatusText}
          </div>

          <div className={styles.meta}>
            {article && <span>Код: {article}</span>}
            {category?.name && <span>{category.name}</span>}
          </div>

          <div className={styles.actions}>
            <button type="button" onClick={addToCart} className={styles.buy} disabled={!canPurchase}>
              Купити
            </button>
            <button
              type="button"
              className={`${styles.favourite} ${isFavorite ? styles.favouriteActive : ''}`}
              onClick={handleFavoriteToggle}
              disabled={favoriteBusy}
              aria-pressed={isFavorite}
            >
              {favoriteBusy ? 'Збереження...' : isFavorite ? 'У вибраному' : 'До улюбленого'}
            </button>
          </div>

          {favoriteNotice && (
            <p className={styles.favoriteNotice} role="status">
              {favoriteNotice}
            </p>
          )}

          {giftProducts.length > 0 && (
            <div className={styles.gifts}>
              <h3 className={styles.giftsTitle}>У подарунок</h3>
              <div className={styles.giftsList}>
                {giftProducts.map((gift, index) => (
                  <div key={`gift-${index}-${gift.id}`} className={styles.giftCard}>
                    <div className={styles.giftImage}>
                      {gift.images?.[0] && (
                        <img src={assetUrl(gift.images[0])} alt={gift.title} />
                      )}
                    </div>
                    <div className={styles.giftInfo}>
                      <div className={styles.giftName}>{gift.title}</div>
                      {gift.article && (
                        <div className={styles.giftArticle}>Код: {gift.article}</div>
                      )}
                      <span className={styles.giftBadge}>Подарунок</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.infoPanel}>
            <p>Оплата при отриманні, карткою або накладений платіж.</p>
            <p>Доставка Новою Поштою, Укрпоштою та кур&apos;єром.</p>
          </div>

          <div className={styles.links}>
            <Link to="/">Умови оплати і доставки</Link>
            <Link to="/">Повернутися до магазину</Link>
          </div>
        </div>
      </div>

      {description && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>Опис</div>
          <div className={styles.sectionBody}>
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        </div>
      )}

      {specs.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>Характеристики</div>
          <div className={styles.sectionBody}>
            <table className={styles.specTable}>
              <tbody>
                {specs.map((row) => (
                  <tr key={row.label}>
                    <th>{row.label}</th>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ProductReviews productId={id} />
    </section>
  );
};

export default Product;
